import AnalysisService from '@/services/analysis';
import { toStructuredFormulation } from '@/modules/formulary/utils/formulationString';

// Module-local, not state: bookkeeping no view should read. Each async action
// takes a monotonic token and discards a response whose token is no longer
// current, so a slow first response can't overwrite a fast second one.
let summaryToken = 0;
const sectionTokens = new Map();
let drugScoreToken = 0;

// Default impact summary — also the shape every consumer can rely on.
export function defaultImpactSummary() {
  return {
    waste: { intValue: 0, isIncrease: false, text: 'waste' },
    infusionsDispensed: { intValue: 0, isIncrease: false, text: 'infusions dispensed' },
    unitsDispensed: { intValue: 0, isIncrease: false, text: 'units dispensed' },
    pctPatientsWithMoreInfusions: { intValue: 0, isIncrease: false, text: '' },
    cost: { stringValue: '$0', isIncrease: false, text: 'Medication Cost' },
  };
}

// Normalize onto the five keys the UI reads, tolerating the legacy "*Containers"
// naming, and coerce numerics so a float can't fail the int32 proto write on save.
function normalizeSummary(raw) {
  const base = defaultImpactSummary();
  if (!raw || typeof raw !== 'object') return base;
  const pick = (...candidates) => candidates.find((c) => c != null);
  const summary = {
    waste: pick(raw.waste, base.waste),
    infusionsDispensed: pick(raw.infusionsDispensed, raw.containersDispensed, base.infusionsDispensed),
    unitsDispensed: pick(raw.unitsDispensed, base.unitsDispensed),
    pctPatientsWithMoreInfusions: pick(
      raw.pctPatientsWithMoreInfusions,
      raw.pctPatientsWithMoreContainers,
      base.pctPatientsWithMoreInfusions
    ),
    cost: pick(raw.cost, base.cost),
  };
  Object.keys(summary).forEach((key) => {
    const metric = { ...summary[key] };
    if (metric.intValue != null) metric.intValue = Math.round(Number(metric.intValue) || 0);
    if (metric.stringValue != null && typeof metric.stringValue !== 'string') {
      metric.stringValue = String(metric.stringValue);
    }
    summary[key] = metric;
  });
  return summary;
}

// Against the real endpoint this is a passthrough; the demo returns raw /query
// rows that still need the concentration/volume split.
function shapeFormulationRow(row) {
  if (row.formulation && row.concentrationValue !== undefined) {
    return row;
  }
  const parts = String(row.formulation || '').split(' in ');
  const concMatch = (parts[0] || '').match(/([\d.]+)\s*(\S+)\/mL/);
  const volMatch = (parts[1] || '').match(/([\d.]+)\s*(\w+)/);
  return {
    formulation: row.formulation,
    infusions: row.infusions || 0,
    // The infusion-weighted waste ratio — the same field Investigate reads, so
    // the table column agrees with the impact tiles beside it.
    wastePercentage:
      row.wasteWeightedPct ?? (row.waste_weighted_pct != null ? Math.round(row.waste_weighted_pct) : null),
    isRTU: false,
    concentrationValue: concMatch ? parseFloat(concMatch[1]) : null,
    concentrationUnit: concMatch ? concMatch[2] : null,
    volumeValue: volMatch ? parseFloat(volMatch[1]) : null,
    volumeUnit: volMatch ? volMatch[2] : 'mL',
    adultInfusions: row.adult_infusions || 0,
    pediatricInfusions: row.pediatric_infusions || 0,
  };
}

// The body both impact endpoints take. Each change carries the canonical string
// AND a structured form, so the server needn't re-implement the parsing regex;
// null means unparseable, which the server should skip rather than guess at.
function toScenarioBody(scope, pairs) {
  return {
    medicationName: scope.medicationName,
    dateRange: { start: scope.startDate || '', end: scope.endDate || '' },
    filters: {
      facilities: scope.facilities || [],
      patientPopulations: scope.patientPopulations || [],
    },
    changes: (pairs || []).map(({ currentFormulation, updatedFormulation }) => ({
      currentFormulation,
      updatedFormulation,
      current: toStructuredFormulation(currentFormulation),
      updated: toStructuredFormulation(updatedFormulation),
    })),
  };
}

export default {
  state: {
    cumulativeImpacts: null,
    cumulativeImpactsLoaded: true,
    formulationData: [],
    formulationDataLoaded: false,
    // Scope every query is run against, set by the view before dispatching a load.
    scope: {
      medicationName: '',
      startDate: '',
      endDate: '',
      facilities: [],
      patientPopulations: [],
    },
    // { [sectionId]: Array<{ currentWaste, updatedWaste, ... }> }
    sectionMetrics: {},
    // The header's Drug Score shows on every formulary step, so it keeps its own
    // copy of the rows rather than borrowing Analyze's, which only exists while
    // that page is mounted.
    drugScoreFormulations: [],
    drugScoreMedication: '',
    // Published by Analyze so the header score reflects the scenario; {} elsewhere.
    proposedWasteByFormulation: {},
  },
  mutations: {
    RESET_ANALYSIS(state) {
      state.cumulativeImpacts = null;
      state.cumulativeImpactsLoaded = true;
      state.formulationData = [];
      state.formulationDataLoaded = false;
      state.sectionMetrics = {};
      state.proposedWasteByFormulation = {};
    },
    SET_DRUG_SCORE_FORMULATIONS(state, { medicationName, rows }) {
      state.drugScoreMedication = medicationName;
      state.drugScoreFormulations = rows;
    },
    SET_ANALYSIS_PROPOSED_WASTE(state, wasteByFormulation) {
      state.proposedWasteByFormulation = wasteByFormulation || {};
    },
    CLEAR_ANALYSIS_SECTION_METRICS(state, sectionId) {
      const next = { ...state.sectionMetrics };
      delete next[sectionId];
      state.sectionMetrics = next;
    },
    SET_ANALYSIS_CUMULATIVE_IMPACTS(state, impacts) {
      state.cumulativeImpacts = impacts;
    },
    SET_ANALYSIS_CUMULATIVE_IMPACTS_LOADED(state, loaded) {
      state.cumulativeImpactsLoaded = loaded;
    },
    SET_ANALYSIS_FORMULATION_DATA(state, formulationData) {
      state.formulationData = formulationData;
    },
    SET_ANALYSIS_FORMULATION_DATA_LOADED(state, loaded) {
      state.formulationDataLoaded = loaded;
    },
    SET_ANALYSIS_SCOPE(state, scope) {
      state.scope = { ...state.scope, ...scope };
    },
    SET_ANALYSIS_SECTION_METRICS(state, { sectionId, metrics }) {
      state.sectionMetrics = { ...state.sectionMetrics, [sectionId]: metrics };
    },
  },
  actions: {
    resetAnalysis({ commit }) {
      // Invalidate anything in flight so a late response can't repopulate a
      // medication the user has already navigated away from.
      summaryToken += 1;
      sectionTokens.clear();
      commit('RESET_ANALYSIS');
    },
    // `costOverride` is the client-side cost delta; user-entered $/infusion is
    // the only cost source, so the server has no price data.
    setAnalysisCumulativeImpacts({ commit, state }, { pairs, costOverride } = {}) {
      const token = ++summaryToken;
      const finish = (summary) => {
        if (summaryToken !== token) return null;
        const next = costOverride ? { ...summary, cost: costOverride } : summary;
        commit('SET_ANALYSIS_CUMULATIVE_IMPACTS', next);
        commit('SET_ANALYSIS_CUMULATIVE_IMPACTS_LOADED', true);
        return next;
      };
      if (!pairs || pairs.length === 0) {
        return Promise.resolve(finish(defaultImpactSummary()));
      }
      commit('SET_ANALYSIS_CUMULATIVE_IMPACTS_LOADED', false);
      return AnalysisService.getAnalysisSummary(toScenarioBody(state.scope, pairs))
        .then((response) => finish(normalizeSummary(response)))
        .catch((error) => {
          if (summaryToken !== token) return null;
          // eslint-disable-next-line no-console
          console.error('Failed to compute cumulative impacts:', error);
          return finish(defaultImpactSummary());
        });
    },
    // Also the gate for the rest of the page: no formulations, no analysis.
    setAnalysisFormulationData({ commit, state }) {
      commit('SET_ANALYSIS_FORMULATION_DATA_LOADED', false);
      return new Promise((resolve, reject) => {
        AnalysisService.getFormulations(state.scope)
          .then((response) => {
            const rows = response.formulations || response.results || [];
            commit('SET_ANALYSIS_FORMULATION_DATA', rows.map(shapeFormulationRow));
            resolve();
          })
          .catch((error) => {
            // eslint-disable-next-line no-console
            console.error('Failed to load formulations:', error);
            commit('SET_ANALYSIS_FORMULATION_DATA', []);
            reject(error);
          })
          .finally(() => {
            commit('SET_ANALYSIS_FORMULATION_DATA_LOADED', true);
          });
      });
    },
    setAnalysisScope({ commit }, scope) {
      commit('SET_ANALYSIS_SCOPE', scope);
    },
    // Clears immediately on a medication change so the previous drug's score
    // never shows under the new name; a filter change keeps it until the reload lands.
    loadDrugScoreFormulations({ commit, state }, scope) {
      const token = ++drugScoreToken;
      const medicationName = scope?.medicationName || '';
      if (medicationName !== state.drugScoreMedication) {
        commit('SET_DRUG_SCORE_FORMULATIONS', { medicationName, rows: [] });
      }
      if (!medicationName) return Promise.resolve([]);
      return AnalysisService.getFormulations(scope)
        .then((response) => (response.formulations || response.results || []).map(shapeFormulationRow))
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Failed to load drug score formulations:', error);
          return [];
        })
        .then((rows) => {
          if (drugScoreToken !== token) return null;
          commit('SET_DRUG_SCORE_FORMULATIONS', { medicationName, rows });
          return rows;
        });
    },
    setAnalysisProposedWaste({ commit }, wasteByFormulation) {
      commit('SET_ANALYSIS_PROPOSED_WASTE', wasteByFormulation);
    },
    // `pairs` is [{ currentFormulation, updatedFormulation }] in ROW ORDER; the
    // response is positional, so the caller can zip it back onto its rows.
    setAnalysisSectionMetrics({ commit, state }, { sectionId, pairs }) {
      if (!pairs || pairs.length === 0) {
        commit('CLEAR_ANALYSIS_SECTION_METRICS', sectionId);
        return Promise.resolve([]);
      }
      const token = (sectionTokens.get(sectionId) || 0) + 1;
      sectionTokens.set(sectionId, token);
      return AnalysisService.getFormularyAnalysis(toScenarioBody(state.scope, pairs))
        .then((response) => {
          if (sectionTokens.get(sectionId) !== token) return null;
          const metrics = response.results || [];
          commit('SET_ANALYSIS_SECTION_METRICS', { sectionId, metrics });
          return metrics;
        })
        .catch((error) => {
          if (sectionTokens.get(sectionId) !== token) return null;
          // eslint-disable-next-line no-console
          console.error('Failed to compute section metrics:', error);
          commit('SET_ANALYSIS_SECTION_METRICS', { sectionId, metrics: [] });
          return [];
        });
    },
  },
  getters: {
    // Always a usable summary, so consumers never branch on null.
    analysisCumulativeImpacts: (state) => state.cumulativeImpacts || defaultImpactSummary(),
    analysisCumulativeImpactsLoaded: (state) => state.cumulativeImpactsLoaded,
    analysisFormulationData: (state) => state.formulationData,
    analysisFormulationDataLoaded: (state) => state.formulationDataLoaded,
    // Formulation strings meeting a min-infusions threshold — the "Your
    // Formulations" picker list and the drug-score qualifying set.
    analysisQualifyingFormulations: (state) => (minInfusions) =>
      state.formulationData.filter((fd) => (fd.infusions || 0) >= (minInfusions ?? 5)),
    analysisScope: (state) => state.scope,
    analysisSectionMetrics: (state) => state.sectionMetrics,
    drugScoreFormulations: (state) => state.drugScoreFormulations,
    analysisProposedWasteByFormulation: (state) => state.proposedWasteByFormulation,
  },
};
